/**
 * CalendarDayCell — renderiza el contenido de UNA celda (un día) dentro
 * del Calendar de Ant Design en la vista mensual del CLIENTE
 * (`calendarioClienteMonth.tsx`). Extraído en round 2026-04-27 (Fase 8)
 * para bajar el tamaño del archivo padre y aislar la lógica de
 * posicionamiento por tercios + lanes que es la parte más densa del módulo.
 *
 * QUÉ HACE
 *   1. Calcula qué "spans" (arriendos / bloqueos / mantenciones / aseos)
 *      tocan este día.
 *   2. Para cada span, obtiene su lane (carril vertical) y segmento dentro
 *      de la semana actual desde `weekLaneCache`.
 *   3. Renderiza UNA barra por span SOLO en su `segStart` (primer día de la
 *      semana donde aparece) — la barra ocupa ancho real (cellsUsed) para
 *      que el label tenga espacio horizontal completo.
 *   4. Si el día no tiene NINGÚN span, muestra el precio por noche (si
 *      existe en `nightPrices`) o un overlay tachado (si es pasado).
 *
 * POR QUÉ ESTÁ AQUÍ Y NO INLINE EN EL PADRE
 *   - El JSX inline rondaba ~270 LOC de un solo callback, hostil a leer.
 *   - El componente recibe sólo data ya calculada (allSpans, lane cache,
 *     prices) → se puede testear/renderizar aislado.
 *   - Comportamiento idéntico al inline: misma firma de `dateCellRender`,
 *     `({ value }) => <CalendarDayCell value={value} ...deps />`.
 *
 * NO VIVE EN HELPERS PORQUE depende de React/AntD (Tooltip, JSX) y de
 * estilos CSS (`cal-book-bar`, `cal-day-stack`). Los helpers puros
 * (`buildWeekLanes`, `clampDay`, etc.) sí están en
 * `calendarioClienteMonth.helpers.ts`.
 */

import React from "react";
import { Tooltip } from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  ISO_FMT,
  moneyFmt,
  type DaySpan,
  isoDate,
  clampDay,
  isMergedEmpty,
  buildWeekLanes,
} from "../calendarioClienteMonth.helpers";
import type { CalendarCellData } from "../useCalendarioLogic";
import { ahoraChile } from "../../../utils/fechaChile";

// ─── Constantes de layout vertical de las barras ─────────────────────────
//
// Estas magic numbers vienen de calibración visual con la altura real de
// las celdas del Calendar de AntD (~120px). Si cambia el tamaño de celda
// (por ejemplo en una nueva variante mobile), revisar también `cal-day-*`
// en `calendario.css`.

/** Offset desde el top de la celda hasta la primera barra (px). Deja
 *  espacio arriba para el número del día y eventuales pills. */
const BAR_TOP = 32;
/** Altura de cada barra (px). */
const BAR_H = 18;
/** Gap vertical entre lanes (px). */
const BAR_GAP = 6;
/** Bleed horizontal (px) que extiende la barra hacia el borde de la
 *  semana cuando el span continúa en otra semana — evita el gap visual
 *  entre filas del calendario mensual. */
const OVERLAP = 7;
/** Máximo de lanes visibles antes de mostrar contador `+N`. Más allá
 *  de 3 carriles, las barras quedan ilegibles dentro de una celda. */
const MAX_LANES = 3;

// ─── Tipos de props ─────────────────────────────────────────────────────

/**
 * Tipo del cache de lanes — derivado de `buildWeekLanes` para que cualquier
 * cambio en su retorno se propague aquí sin tocar este archivo.
 */
type WeekLaneCache = Map<string, ReturnType<typeof buildWeekLanes>>;

interface CalendarDayCellProps {
  /** Día a renderizar (lo provee Ant Design Calendar via dateCellRender). */
  value: Dayjs;
  /** ID de la propiedad actualmente seleccionada en el toolbar. Si es
   *  `null` (no hay propiedad seleccionada) la celda renderiza vacía. */
  selectedPropertyId: number | null;
  /** Color sólido (hex/rgb) usado para barras de arriendo de esta
   *  propiedad. Bloqueos/mantenciones usan colores semánticos en CSS. */
  selectedPropertyColor: string;
  /** Mapa ISO → datos consolidados de la celda (para detectar si el día
   *  está realmente vacío via `isMergedEmpty`). */
  mergedByDate: Record<string, CalendarCellData | undefined>;
  /** Precios por noche por propiedad y fecha. Estructura:
   *  `nightPrices[propertyId][isoDate] = pesos`. */
  nightPrices: Record<number, Record<string, number>> | null;
  /** TODOS los spans del rango cargado (arriendos + bloqueos + aseos). El
   *  componente filtra los que tocan este día. */
  allSpans: DaySpan[];
  /** Cache de lanes por semana — pre-computado en el padre vía
   *  `buildWeekLanes`. Se indexa por ISO del lunes de la semana. */
  weekLaneCache: WeekLaneCache;
}

// ─── Componente ─────────────────────────────────────────────────────────

const CalendarDayCell: React.FC<CalendarDayCellProps> = ({
  value,
  selectedPropertyId,
  selectedPropertyColor,
  mergedByDate,
  nightPrices,
  allSpans,
  weekLaneCache,
}) => {
  // Sin propiedad seleccionada no hay nada que renderizar — el toolbar
  // padre muestra un placeholder en este caso.
  if (!selectedPropertyId) return null;

  const iso = value.format(ISO_FMT);
  const merged = mergedByDate[iso];
  const today = ahoraChile();

  // ─── 1) Calcular barras activas en este día ─────────────────────────
  //
  // Para cada span del rango, decidimos si toca este día (entre
  // barStartISO y barEndISO inclusive) y, si toca, traemos su lane y
  // segmento desde el cache pre-computado de la semana.
  const weekStart = value.startOf("week");
  const weekEnd = weekStart.add(6, "day");
  const laneInfo = weekLaneCache.get(isoDate(weekStart));

  const activeBookings = allSpans
    .map((s) => {
      const start = dayjs(s.barStartISO);
      const end = dayjs(s.barEndISO);

      // Skip: el día actual no está dentro del span.
      if (value.isBefore(start, "day")) return null;
      if (value.isAfter(end, "day")) return null;

      const seg = laneInfo?.segByKey.get(s.key);
      const lane = laneInfo?.laneByKey.get(s.key) ?? 0;

      // Fallback si no hay laneInfo (no debería pasar pero por defensa):
      // clampeamos el span al rango de la semana visible.
      const segStart = seg?.segStart ?? clampDay(start, weekStart, weekEnd);
      const segEnd = seg?.segEnd ?? clampDay(end, weekStart, weekEnd);

      const isSegStart = value.isSame(segStart, "day");
      const isSegEnd = value.isSame(segEnd, "day");

      const isRealStart = value.isSame(start, "day");
      const isRealEnd = value.isSame(end, "day");
      const startsBeforeWeek = start.isBefore(weekStart, "day");
      const endsAfterWeek = end.isAfter(weekEnd, "day");

      return {
        ...s,
        lane,
        isSegStart,
        isSegEnd,
        isRealStart,
        isRealEnd,
        startsBeforeWeek,
        endsAfterWeek,
      };
    })
    .filter(Boolean) as (DaySpan & {
    lane: number;
    isSegStart: boolean;
    isSegEnd: boolean;
    isRealStart: boolean;
    isRealEnd: boolean;
    startsBeforeWeek: boolean;
    endsAfterWeek: boolean;
  })[];

  // ─── 2) Día sin barras: mostrar precio o tachado ────────────────────
  //
  // Antes los aseos se mostraban como badge separado, lo que generaba una
  // "línea" extra por día aunque no solaparan con nada. Ahora TODOS los
  // eventos (arriendos / bloqueos / mantenciones / aseos) viven en el
  // mismo sistema de lanes — un aseo de mediodía comparte lane con un
  // check-out matutino y un check-in vespertino del mismo día (tercios
  // distintos, mismo carril). Misma lógica que el calendario admin.
  const hasBars = activeBookings.length > 0;

  if (!hasBars) {
    const isPast = value.isBefore(today, "day");
    const price = nightPrices?.[selectedPropertyId]?.[iso];

    if (price != null) {
      return (
        <div className="cal-day-center">
          <div className="cal-day-price">${moneyFmt.format(price)}</div>
        </div>
      );
    }
    return isPast ? <div className="cal-day-past-empty-overlay" /> : null;
  }

  // ─── 3) Filtrar barras a renderizar ─────────────────────────────────
  //
  // CRÍTICO: en el Calendar de AntD, dateCellRender se ejecuta por celda
  // (un día). Si renderizamos una barra "por día" y solo mostramos el
  // label en el primer día, el texto queda limitado al ancho de esa celda
  // y se corta. Solución: renderizar SOLO al inicio del segmento semanal
  // (segStart) y darle ancho real (cellsUsed) para que el label tenga
  // todo el espacio horizontal del segmento.
  const segStarts = activeBookings.filter((b) => b.isSegStart);

  const visibleBars = segStarts
    .filter((b) => b.lane < MAX_LANES)
    .sort((a, b) => a.lane - b.lane);

  const hiddenCount = Math.max(0, segStarts.length - visibleBars.length);

  // Defensive: aunque ya sabemos que hasBars=true, si por algún motivo
  // todos los spans empezaron antes de esta semana y NO tienen segStart
  // aquí, no rendereamos contenedor vacío para no consumir altura.
  if (visibleBars.length === 0 && hiddenCount === 0) {
    return null;
  }

  // Desuso intencional: usamos `merged` solo para una decisión futura —
  // hoy día la verificación de "no vacío" para click vive en el padre
  // (`onSelect`). Lo dejamos accesible por si en el futuro queremos
  // overlay aquí también. Mantenemos la lectura para que TS no se queje
  // de import sin uso.
  void isMergedEmpty(merged);

  // ─── 4) Render ──────────────────────────────────────────────────────
  return (
    <div className="cal-day-stack">
      {hiddenCount > 0 && (
        <div
          className="cal-book-overflow"
          title={`${hiddenCount} arriendos más`}
        >
          +{hiddenCount}
        </div>
      )}

      {visibleBars.map((b) => {
        // Recalcular el segmento dentro de la semana para obtener
        // cuántas celdas abarca la barra desde aquí en adelante.
        const start = dayjs(b.barStartISO);
        const end = dayjs(b.barEndISO);

        const seg = laneInfo?.segByKey.get(b.key);
        const segStart = seg?.segStart ?? clampDay(start, weekStart, weekEnd);
        const segEnd = seg?.segEnd ?? clampDay(end, weekStart, weekEnd);

        const startsBeforeWeek = start.isBefore(weekStart, "day");
        const endsAfterWeek = end.isAfter(weekEnd, "day");

        // Posicionamiento por TERCIOS — ver docs/calendario-logica.md §2.
        //   leftFrac : fracción del primer cell donde arranca la barra
        //              (0 | 1/3 | 2/3). Si el inicio real es semana
        //              previa → 0 (arranca en borde izquierdo).
        //   rightFrac: fracción del último cell donde termina la barra
        //              (1/3 | 2/3 | 1). Si continúa a la siguiente
        //              semana → 1 (termina en borde derecho).
        const leftFrac = startsBeforeWeek ? 0 : b.startFrac;
        const rightFrac = endsAfterWeek ? 1 : b.endFrac;

        // Celdas cubiertas + ajuste fraccional.
        //   cellsUsed = (segEnd - segStart) + rightFrac - leftFrac
        // Ejemplo: bloqueo evening A → morning B (1 noche) →
        //   segStart=A, segEnd=B, leftFrac=2/3, rightFrac=1/3,
        //   cellsUsed = 1 + 1/3 - 2/3 = 2/3 (de una celda de ancho),
        //   y left = 66.67% del primer cell.
        const cellsDiff = segEnd.diff(segStart, "day");
        const cellsUsed = cellsDiff + rightFrac - leftFrac;

        // Bleed horizontal: extiende la barra unos px hacia el borde
        // cuando el segmento continúa en otra semana. Sin este bleed
        // queda un gap visual entre filas del calendario mensual.
        const widthAdjPx =
          (startsBeforeWeek ? OVERLAP : 0) + (endsAfterWeek ? OVERLAP : 0);

        // left: negativo si viene de antes (bleed hacia afuera del
        // primer cell); fracción del primer cell en caso normal.
        const left = startsBeforeWeek
          ? `-${OVERLAP}px`
          : `${leftFrac * 100}%`;

        const width = `calc(${cellsUsed} * 100% + ${widthAdjPx}px)`;

        // Marcas de inicio/fin REAL del span (no del segmento). Si la
        // barra empieza/termina en esta semana, marca con IN/OUT; si
        // continúa hacia atrás/adelante, usa flecha ◀/▶.
        const isRealStart = segStart.isSame(start, "day");
        const isRealEnd = segEnd.isSame(end, "day");

        const leftMark = isRealStart ? "IN" : startsBeforeWeek ? "◀" : "";
        const rightMark = isRealEnd ? "OUT" : endsAfterWeek ? "▶" : "";

        // Solo renderizamos la barra en segStart, así que siempre
        // mostramos el label aquí (el ancho ya cubre todo el segmento).
        const showLabel = true;

        // Clases de estilo:
        //  - kindClass: tipo (booking | block | maintenance | cleaning).
        //  - colorClass `is-from-client`: bloqueos/mantenciones creados
        //    por el cliente usan color CLARO; los creados por admin usan
        //    color OSCURO (mismo color entre vista cliente y vista admin
        //    para coherencia visual).
        //  - readOnlyClass `is-read-only`: barras que el cliente NO puede
        //    editar (cursor: not-allowed). Aplica a aseos siempre, y a
        //    bloqueos/mantenciones que no son del propio cliente.
        const kindClass =
          b.kind === "booking"
            ? "is-kind-booking"
            : b.kind === "maintenance"
              ? "is-kind-maintenance"
              : b.kind === "cleaning"
                ? "is-kind-cleaning"
                : "is-kind-block";
        const colorClass =
          b.kind !== "booking" && b.kind !== "cleaning" && b.isFromClient
            ? "is-from-client"
            : "";
        const readOnlyClass =
          b.kind === "cleaning" || (b.kind !== "booking" && !b.isOwn)
            ? "is-read-only"
            : "";

        return (
          <Tooltip
            key={b.key}
            title={b.tooltip ?? b.label}
            mouseEnterDelay={0.2}
            overlayClassName="cal-bar-tooltip"
            placement="top"
          >
            <div
              className={
                "cal-book-bar " +
                kindClass +
                (colorClass ? " " + colorClass : "") +
                (readOnlyClass ? " " + readOnlyClass : "") +
                (startsBeforeWeek ? " is-mid-left" : " is-start") +
                (endsAfterWeek ? " is-mid-right" : " is-end")
              }
              style={{
                top: BAR_TOP + b.lane * (BAR_H + BAR_GAP),
                left,
                width,
                // Color sólido por propiedad (igual que admin). Solo
                // aplica a arriendos; bloqueos/mantenciones usan su
                // color semántico definido en CSS por kindClass.
                ...(b.kind === "booking"
                  ? { backgroundColor: selectedPropertyColor }
                  : {}),
              }}
            >
              {leftMark ? (
                <span className="cal-book-mark left">{leftMark}</span>
              ) : null}
              {showLabel ? (
                <span className="cal-book-label-float">{b.label}</span>
              ) : null}
              {rightMark ? (
                <span className="cal-book-mark right">{rightMark}</span>
              ) : null}
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default CalendarDayCell;
